var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { MessageHandler } from "../module";
import { ModuleId } from "flavr-common";
import { Group, Vector3, Quaternion, Mesh, MeshBasicMaterial, DoubleSide, ShapeGeometry } from "three";
import { localPlayerContainer } from "../localplayer/LocalPlayerContainer";
import { now } from "../../messages/timing";
import { localizationService } from "../localization";
import { camera } from "../rendering/camera";
import { renderManager } from "../xr/RenderManager";
import { VrToastMessage } from "../../messages/xr/WebXRMessages";
import { xrService } from "../xr/XRService";
import { Text } from "troika-three-text";
import { roundedRect } from "../rendering/speechBubble/speechBubbleGeometry";
import { NewModule, PresentationMode } from "../module/NewModule";
import { RenderOrder } from "../rendering/RenderOrder";
const TOAST_SCALE = new Vector3(0.1, 0.1, 0.1);
const TOAST_ORIENTATION = new Quaternion(0, 1, 0, 0);
const TOAST_OFFSET = 0.06;
const MAX_TOAST_TEXT_WIDTH = 2.0; // width used for determining the word wrap boundaries
const TEXT_BACKGROUND_MARGIN = 0.05;
const TEXT_BACKGROUND_OUTLINE_WIDTH = 0.015;
const MAX_NUM_TOASTS = 3; // the number of toasts that can be displayed simultaneously
const TOAST_DURATION = 3000; // how long toast are visible (in ms)
const TOAST_RELATIVE_POSITION_VR = new Vector3(0, -0.15, -0.5);
const TOAST_RELATIVE_POSITION_DESKTOP = new Vector3(0, -0.18, -0.5);
function getVisibleTextBounds(text) {
    const info = text.textRenderInfo;
    if (typeof info !== "object" || info === null || !("visibleBounds" in info))
        return undefined;
    const bounds = info.visibleBounds;
    if (!Array.isArray(bounds) || bounds.length < 4 || !bounds.slice(0, 4).every((value) => typeof value === "number"))
        return undefined;
    return [bounds[0], bounds[1], bounds[2], bounds[3]];
}
/**
 * Displays short-lived toast messages in front of the user so XR and desktop notifications remain readable inside the 3D scene.
 *
 * Messages:
 * - `VrToastMessage` - Queues a new toast notification with optional duration, color and message identifier.
 */
export class VrToastModule extends NewModule {
    /**
     * Creates the toast anchor group (excluded from frustum culling and matrix auto-update since its transform
     * is updated manually every frame) and attaches it to the local player's root node.
     */
    constructor() {
        super(ModuleId.VRTOAST);
        this.pendingToasts = [];
        this.vrToasts = [];
        this.group = new Group();
        this.lastUpdateTime = 0;
        this.group.frustumCulled = false;
        this.group.matrixAutoUpdate = false;
        localPlayerContainer.localPlayerRootNode.add(this.group);
    }
    /// Life cycle methods ///
    onPreRender() {
        var _a;
        const time = now();
        const dt = time - this.lastUpdateTime;
        this.lastUpdateTime = time;
        if (this.vrToasts.length === 0 && this.pendingToasts.length === 0)
            return;
        // Remove expired toasts
        if (this.vrToasts.length > 0) {
            const oldest = this.vrToasts[0];
            const timeToShowInMs = (_a = oldest.timeToShowInMs) !== null && _a !== void 0 ? _a : TOAST_DURATION;
            if (time - oldest.time > timeToShowInMs) {
                this.removeToast(oldest);
            }
        }
        // Animate movement
        for (const toast of this.vrToasts) {
            if (toast.currentPos !== toast.targetPos) {
                toast.currentPos = Math.min(toast.targetPos, toast.currentPos + dt / 2000);
                toast.group.position.y = toast.currentPos;
            }
        }
        // Create new toasts from pending list
        let startOffset = -TOAST_OFFSET;
        while (this.vrToasts.length < MAX_NUM_TOASTS && this.pendingToasts.length > 0) {
            const textContainer = this.create3DText(startOffset);
            const nextToast = this.pendingToasts.shift();
            if (nextToast === undefined)
                break;
            // Move existing ones upward
            for (const toast of this.vrToasts) {
                toast.targetPos += TOAST_OFFSET;
            }
            this.vrToasts.push({
                group: textContainer.group,
                text: textContainer.text,
                background: textContainer.background,
                outline: textContainer.outline,
                time,
                currentPos: startOffset,
                targetPos: 0,
                timeToShowInMs: nextToast.timeToShowInMs,
                messageID: nextToast.messageID
            });
            startOffset -= TOAST_OFFSET;
        }
        // Follow camera
        const pos = this.group.position.copy(renderManager.isXRPresenting()
            ? TOAST_RELATIVE_POSITION_VR
            : TOAST_RELATIVE_POSITION_DESKTOP);
        pos.applyQuaternion(camera.perspectiveCam.quaternion);
        pos.add(camera.perspectiveCam.position);
        this.group.quaternion.copy(camera.perspectiveCam.quaternion);
        this.group.updateMatrix();
    }
    onPresentationModeChanged(presentationMode) {
        if (presentationMode !== PresentationMode.VR) {
            while (this.vrToasts.length > 0) {
                this.removeToast(this.vrToasts[0]);
            }
        }
    }
    /// Message handlers ///
    handleMessage(msg) {
        if (!xrService.isPresenting()) {
            console.error("Toast message ignored, because XR is not presenting");
            return;
        }
        const text = localizationService.getTranslation(msg.message);
        // Remove any toasts (active or pending) with same messageID
        if (msg.messageID !== undefined) {
            // Remove from active toasts
            for (const t of this.vrToasts) {
                if (t.messageID === msg.messageID) {
                    this.removeToast(t);
                }
            }
            // Remove from pending queue
            for (let i = this.pendingToasts.length - 1; i >= 0; i--) {
                if (this.pendingToasts[i].messageID === msg.messageID) {
                    this.pendingToasts.splice(i, 1);
                }
            }
        }
        this.pendingToasts.push({
            message: text,
            timeToShowInMs: msg.timeToShowInMs,
            color: msg.color,
            messageID: msg.messageID
        });
    }
    /// Helper methods ///
    create3DText(startOffset) {
        const toastGroup = new Group();
        const text = new Text();
        const background = new Mesh();
        const outline = new Mesh();
        text.text = this.pendingToasts[0].message;
        const colorValue = this.pendingToasts[0].color;
        text.anchorX = "center";
        text.anchorY = "middle";
        text.textAlign = "center";
        text.maxWidth = MAX_TOAST_TEXT_WIDTH;
        text.whiteSpace = "normal";
        text.overflowWrap = "normal";
        text.material.depthTest = false;
        text.material.depthWrite = false;
        text.renderOrder = RenderOrder.UI_ELEMENTS; // must be above the background
        text.scale.copy(TOAST_SCALE);
        text.quaternion.copy(TOAST_ORIENTATION);
        text.rotateY(Math.PI);
        text.position.set(0, 0, 0);
        toastGroup.position.set(0, startOffset, 0);
        toastGroup.add(text);
        text.sync(() => {
            var _a, _b, _c, _d, _e;
            if (text.parent !== toastGroup)
                return;
            const boundingBoxSize = new Vector3();
            const boundingBox = text.geometry.boundingBox;
            boundingBox === null || boundingBox === void 0 ? void 0 : boundingBox.getSize(boundingBoxSize);
            const [visibleMinX, visibleMinY, visibleMaxX, visibleMaxY] = (_a = getVisibleTextBounds(text)) !== null && _a !== void 0 ? _a : [
                (_b = boundingBox === null || boundingBox === void 0 ? void 0 : boundingBox.min.x) !== null && _b !== void 0 ? _b : -boundingBoxSize.x / 2,
                (_c = boundingBox === null || boundingBox === void 0 ? void 0 : boundingBox.min.y) !== null && _c !== void 0 ? _c : -boundingBoxSize.y / 2,
                (_d = boundingBox === null || boundingBox === void 0 ? void 0 : boundingBox.max.x) !== null && _d !== void 0 ? _d : boundingBoxSize.x / 2,
                (_e = boundingBox === null || boundingBox === void 0 ? void 0 : boundingBox.max.y) !== null && _e !== void 0 ? _e : boundingBoxSize.y / 2
            ];
            const width = visibleMaxX - visibleMinX + TEXT_BACKGROUND_MARGIN;
            const height = visibleMaxY - visibleMinY + TEXT_BACKGROUND_MARGIN;
            const outlineWidth = width + TEXT_BACKGROUND_OUTLINE_WIDTH * 2;
            const outlineHeight = height + TEXT_BACKGROUND_OUTLINE_WIDTH * 2;
            outline.geometry = new ShapeGeometry(roundedRect(-outlineWidth / 2, -outlineHeight / 2, outlineWidth, outlineHeight, 0.1));
            outline.material = new MeshBasicMaterial({ color: 0x111827, side: DoubleSide, depthTest: false, depthWrite: false });
            outline.scale.copy(TOAST_SCALE);
            outline.geometry.scale(1.1, 1.1, 1);
            outline.quaternion.copy(text.quaternion);
            outline.position.copy(text.position);
            outline.position.z -= 0.0001;
            outline.renderOrder = RenderOrder.UI_ELEMENTS;
            toastGroup.add(outline);
            background.geometry = new ShapeGeometry(roundedRect(-width / 2, -height / 2, width, height, 0.1));
            background.material = new MeshBasicMaterial({ color: colorValue !== undefined ? colorValue : 0x336699, side: DoubleSide, depthTest: false, depthWrite: false, transparent: true });
            background.scale.copy(TOAST_SCALE);
            background.geometry.scale(1.1, 1.1, 1); // Reset scale to avoid double scaling
            background.quaternion.copy(text.quaternion);
            background.position.copy(text.position);
            background.position.z -= 0.0001;
            background.renderOrder = RenderOrder.UI_ELEMENTS;
            toastGroup.add(background);
            background.updateMatrix();
        });
        this.group.add(toastGroup);
        return { group: toastGroup, text, background, outline };
    }
    removeToast(toast) {
        // Capture target position of the one being removed
        const removedTarget = toast.targetPos;
        this.vrToasts.splice(this.vrToasts.indexOf(toast), 1);
        this.group.remove(toast.group);
        toast.background.geometry.dispose();
        toast.outline.geometry.dispose();
        toast.text.geometry.dispose();
        const disposeMat = (m) => {
            if (Array.isArray(m))
                for (const mm of m)
                    mm.dispose();
            else
                m.dispose();
        };
        disposeMat(toast.text.material);
        disposeMat(toast.background.material);
        disposeMat(toast.outline.material);
        // Move all messages above this message down
        for (const t of this.vrToasts) {
            if (t.targetPos > removedTarget) {
                t.targetPos -= TOAST_OFFSET;
                // (do NOT snap currentPos; your animation loop will ease it down)
            }
        }
    }
}
__decorate([
    MessageHandler(VrToastMessage)
], VrToastModule.prototype, "handleMessage", null);
